import React from 'react';
import { Chip, Tooltip, Box } from '@mui/material';
import {
  Person as UserIcon,
  Settings as SystemIcon,
  CloudDownload as ApiIcon,
  Upload as ImportIcon,
  MergeType as MergeIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { ContributionSource, ContributionSourceDetails } from '../../types';

/**
 * SourceBadge - Color-coded badge showing the source of a change
 *
 * Color scheme (Phase 4.3):
 * - 🔵 Blue: 'user' - Manual user edits
 * - 🟢 Green: 'api-ancestry', 'api-familysearch' - API integrations
 * - 🟡 Yellow: 'import-gedcom', 'import-csv' - Data imports
 * - 🟠 Orange: 'system' - System-generated changes
 * - 🔴 Red: 'admin-correction' - Admin bulk corrections
 * - ⚪ Gray: 'merge' - Person/entity merges
 */

interface SourceBadgeProps {
  source: ContributionSource;
  sourceDetails?: ContributionSourceDetails;
  size?: 'small' | 'medium';
  showIcon?: boolean;
}

export const SourceBadge: React.FC<SourceBadgeProps> = ({
  source,
  sourceDetails,
  size = 'small',
  showIcon = true,
}) => {
  // ===== Helper Functions =====

  /**
   * Get the color for the source badge
   */
  const getSourceColor = (source: ContributionSource): string => {
    const colorMap: Record<ContributionSource, string> = {
      'user': '#2196F3',              // Blue
      'api-ancestry': '#4CAF50',      // Green
      'api-familysearch': '#4CAF50',  // Green
      'import-gedcom': '#FFC107',     // Yellow/Amber
      'import-csv': '#FFC107',        // Yellow/Amber
      'system': '#FF9800',            // Orange
      'merge': '#9E9E9E',             // Gray
      'admin-correction': '#F44336',  // Red
    };
    return colorMap[source] || '#9E9E9E';
  };

  /**
   * Get the icon for the source
   */
  const getSourceIcon = (source: ContributionSource): React.ReactElement => {
    const iconMap: Record<ContributionSource, React.ReactElement> = {
      'user': <UserIcon fontSize={size} />,
      'api-ancestry': <ApiIcon fontSize={size} />,
      'api-familysearch': <ApiIcon fontSize={size} />,
      'import-gedcom': <ImportIcon fontSize={size} />,
      'import-csv': <ImportIcon fontSize={size} />,
      'system': <SystemIcon fontSize={size} />,
      'merge': <MergeIcon fontSize={size} />,
      'admin-correction': <AdminIcon fontSize={size} />,
    };
    return iconMap[source] || <UserIcon fontSize={size} />;
  };

  /**
   * Get the human-readable label for the source
   */
  const getSourceLabel = (source: ContributionSource): string => {
    const labelMap: Record<ContributionSource, string> = {
      'user': 'User Edit',
      'api-ancestry': 'Ancestry API',
      'api-familysearch': 'FamilySearch API',
      'import-gedcom': 'GEDCOM Import',
      'import-csv': 'CSV Import',
      'system': 'System',
      'merge': 'Merge',
      'admin-correction': 'Admin Correction',
    };
    return labelMap[source] || 'Unknown';
  };

  /**
   * Build the tooltip content with source details
   */
  const getTooltipContent = (): React.ReactNode => {
    const label = getSourceLabel(source);

    // If no source details, just show the label
    if (!sourceDetails || Object.keys(sourceDetails).length === 0) {
      return (
        <Box>
          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{label}</div>
          <div style={{ fontSize: '0.85em', opacity: 0.9 }}>
            {getSourceDescription(source)}
          </div>
        </Box>
      );
    }

    // Build detailed tooltip with source details
    return (
      <Box>
        <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: '0.85em', opacity: 0.9 }}>
          {getSourceDescription(source)}
        </div>
        {sourceDetails && (
          <Box sx={{ mt: 1, fontSize: '0.85em' }}>
            {/* API details */}
            {sourceDetails.apiRequestId && (
              <div>Request: {sourceDetails.apiRequestId}</div>
            )}
            {sourceDetails.apiRecordId && (
              <div>Record: {sourceDetails.apiRecordId}</div>
            )}
            {sourceDetails.apiConfidence !== undefined && (
              <div>Confidence: {sourceDetails.apiConfidence}%</div>
            )}

            {/* Import details */}
            {sourceDetails.importFilename && (
              <div>File: {sourceDetails.importFilename}</div>
            )}
            {sourceDetails.importRowNumber !== undefined && (
              <div>Row: {sourceDetails.importRowNumber}</div>
            )}

            {/* System details */}
            {sourceDetails.systemProcess && (
              <div>Process: {sourceDetails.systemProcess}</div>
            )}

            {/* Merge details */}
            {sourceDetails.mergedFromIds && sourceDetails.mergedFromIds.length > 0 && (
              <div>Merged from: {sourceDetails.mergedFromIds.length} record(s)</div>
            )}

            {/* Generic notes */}
            {sourceDetails.notes && (
              <div style={{ marginTop: 4, fontStyle: 'italic' }}>
                {sourceDetails.notes}
              </div>
            )}
          </Box>
        )}
      </Box>
    );
  };

  /**
   * Get a description of what this source type means
   */
  const getSourceDescription = (source: ContributionSource): string => {
    const descriptionMap: Record<ContributionSource, string> = {
      'user': 'Manual edit by a user',
      'api-ancestry': 'Data retrieved from Ancestry.com',
      'api-familysearch': 'Data retrieved from FamilySearch',
      'import-gedcom': 'Imported from GEDCOM file',
      'import-csv': 'Imported from CSV file',
      'system': 'Automatically calculated by system',
      'merge': 'Result of merging duplicate records',
      'admin-correction': 'Bulk correction by administrator',
    };
    return descriptionMap[source] || 'Unknown source';
  };

  // ===== Render =====

  const color = getSourceColor(source);
  const label = getSourceLabel(source);
  const icon = showIcon ? getSourceIcon(source) : undefined;

  return (
    <Tooltip title={getTooltipContent()} arrow placement="top">
      <Chip
        icon={icon}
        label={label}
        size={size}
        sx={{
          backgroundColor: color,
          color: '#fff',
          fontWeight: 500,
          '& .MuiChip-icon': {
            color: '#fff',
          },
          cursor: 'help',
        }}
      />
    </Tooltip>
  );
};
